import { AnchorHTMLAttributes, FC, MouseEvent, RefObject, useCallback } from 'react';

import { COLOURS, Z_INDEX } from '@belong/themes';

import CONTENT from './content.json';

// Hardcoding styles as this will be injected into window.parent through
// a Portal if chat is iframed (cannot use styled-components).
// Note: these are duplicate styles of ui-components' SkipLink.

const skipLinkClassName = 'belong-chat-skip-link';
const skipLinkStyles = `
  .${skipLinkClassName} {
    position: absolute;
    z-index: ${Z_INDEX.SKIP_LINK};
    top: -100rem;
    left: -100rem;

    background: ${COLOURS.WHITE};
    padding: 0.5rem;
  }

  .${skipLinkClassName}:focus {
    top: 1rem;
    left: 1rem;
  }
`;

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement>;
type RequiredHref = Required<Pick<AnchorProps, 'href'>>;
type SkipLinkProps = AnchorProps & RequiredHref & { targetRef: RefObject<HTMLElement> };

/**
 * Renders an accessible "Skip to chat" link which only appears when tabbed to.
 * Will be rendered into the window.parent if chat is iframed.
 * Requires an href for a11y purposes, but programmatically sets focus to the given targetRef.
 */
export const SkipToChatLink: FC<SkipLinkProps> = ({ targetRef, href, ...anchorProps }) => {
  const handleClick = useCallback((e: MouseEvent) => {
    if (targetRef?.current) {
      e.preventDefault();
      targetRef.current.focus();
    }
  }, []);

  return (
    <>
      <style>{skipLinkStyles}</style>
      {/* Explicitly passing href to ensure jsx-a11y understands it will be present */}
      <a className={skipLinkClassName} onClick={handleClick} href={href} {...anchorProps}>
        {CONTENT.skipToChat}
      </a>
    </>
  );
};
